"use client";

import React, { FC, useEffect } from "react";
import { useParams } from "react-router-dom";

import Skeleton from "@mui/material/Skeleton";
import { useQuery } from "@tanstack/react-query";

import {
  ChevronArrowDownIcon,
  FilterIcon,
  SearchIcon,
} from "../../../assets/svg";
import { PyButton } from "../../../components";
import { CartCard } from "../../../components/cart-card";
import {
  getPayLinkStoreProducts,
  getPayLinkStoreProfile,
} from "../../../services/api";
import { usePayLinkCart } from "../context/PayLinkCartContext";
import { getProducts, getStoreProfile } from "../services/api";
import { IStoreProfile } from "../types/campaigns";

interface IProps {
  storeProfile: IStoreProfile | undefined;
  productVariantUrn?: string;
}

export const ProductsList: FC<IProps> = ({
  storeProfile,
  productVariantUrn,
}) => {
  const { storeSlug } = useParams();
  const { addToCart, pendingProductUrns, setStoreProfile, cartItems } =
    usePayLinkCart();
  const [searchQuery, setSearchQuery] = React.useState("");
  const { data: data_product, isLoading: isProductsLoading } = useQuery({
    queryKey: [
      "getPaylinkProducts",
      productVariantUrn,
      storeProfile?.customerUniqueReferenceNumber,
    ],
    queryFn: () =>
      getProducts({
        CustomerUniqueReferenceNumber:
          storeProfile?.customerUniqueReferenceNumber ?? undefined,
        VariantUniqueReferenceNumber: productVariantUrn ?? undefined,
      }),
    enabled: !!(productVariantUrn || storeProfile?.customerUniqueReferenceNumber),
    refetchOnWindowFocus: false,
  });
  const products = (data_product && data_product.data) ?? [];
  const filteredProducts = React.useMemo(() => {
    if (!products) {
      return [];
    }

    let result = products;

    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter((item: any) => {
        return (
          item.name?.toLowerCase().includes(q) ||
          item.categoryName?.toLowerCase().includes(q) ||
          item.sku?.toLowerCase().includes(q)
        );
      });
    }

    return result;
  }, [products, searchQuery]);

  const { data: store_data, isLoading: store_isLoading } = useQuery({
    queryKey: [
      "GetStoreProfile",
      filteredProducts[0]?.customerUniqueReferenceNumber,
    ],
    queryFn: () =>
      getStoreProfile({
        CustomerUniqueReferenceNumber:
          filteredProducts[0]?.customerUniqueReferenceNumber,
      }),
    enabled: !!productVariantUrn && filteredProducts.length === 1,
  });

  const storeProfileForProduct =
    store_data && store_data?.data && store_data.data[0];
  useEffect(() => {
    if (storeProfileForProduct) {
      setStoreProfile(storeProfileForProduct);
    }
  }, [storeProfileForProduct]);

  const showSkeleton = isProductsLoading;

  return (
    <div className="w-full flex-1 flex flex-col gap-6 !p-5 lg:!p-0 !container mx-auto">
      {filteredProducts.length > 1 && (
        <div className="flex items-start md:items-center justify-between !gap-2 lg:!gap-4 w-full lg:!mt-5">
          <div className="flex items-center !gap-2 lg:!gap-4 w-full md:w-auto flex-1">
            {/* <PyButton
            variant="SecondaryOutline"
            extraClass="!min-w-10 !w-10 !h-10 lg:!w-fit !bg-py_black-50 !border-py_black-50 lg:!border-py_black-950 lg:!bg-transparent"
          >
            <FilterIcon color="currentColor" />
            <span className="hidden lg:inline-block"> All filters</span>
          </PyButton> */}

            <div className="relative flex-1 max-w-[200px] lg:max-w-[400px] h-10">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <SearchIcon width="20" height="20" />
              </div>
              <input
                type="text"
                placeholder="Search products"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="block w-full h-full pl-10 pr-3 py-2 border border-py_black-100 rounded-lg text-sm placeholder-py_black-600 focus:outline-none focus:ring-1 focus:ring-py_black-200 focus:border-py_black-200"
              />
            </div>
          </div>
          {/* <div className="flex items-center gap-2">
            <PyButton
              variant="SecondaryOutline"
              extraClass="!h-10 !bg-py_black-50 !border-py_black-50 lg:!border-py_black-950 lg:!bg-transparent"
            >
              <span className="hidden lg:inline-block"> Sort by: </span>
              Relevance
              <ChevronArrowDownIcon color="currentColor" />
            </PyButton>
          </div> */}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6 w-full">
        {showSkeleton &&
          Array.from({ length: 8 }).map((_, index) => (
            <section
              key={`product-skeleton-${index}`}
              className="border border-py_black-100 rounded-2xl border-solid p-2 bg-white">
              <Skeleton
                variant="rectangular"
                height={220}
                sx={{ borderRadius: "8px" }}
              />
              <Skeleton variant="text" height={30} sx={{ mt: 1 }} />
              <Skeleton variant="text" height={22} width="70%" />
              <Skeleton
                variant="rounded"
                height={40}
                sx={{ mt: 1, borderRadius: "8px" }}
              />
            </section>
          ))}

        {!showSkeleton &&
          filteredProducts &&
          filteredProducts.map((product, index: number) => (
            <CartCard
              key={`${product.productCatalogueItemUniqueReferenceNumber}-${product.variantUniqueReferenceNumber}-${index}`}
              productItem={product}
              onAddToCart={() => addToCart(product)}
              addingToCart={pendingProductUrns.includes(
                product.productCatalogueItemUniqueReferenceNumber,
              )}
            />
          ))}
      </div>

      {!showSkeleton && filteredProducts.length === 0 && (
        <div className="border border-py_black-100 rounded-2xl p-6 text-sm text-py_black-700">
          Product filter was applied, but no matching product was found.
        </div>
      )}
    </div>
  );
};
